// R94 — THE SHELF THAT SAID THE VIDEOS HAD NOT BEEN ADDED YET, WHEN THE DATABASE IS THE THING THAT
// DID NOT ANSWER.
//
// **THE ROUND IS TWO HALVES AND BOTH WERE LIVE.**
//
// (1) **THE SERVER CAUGHT ITS OWN DATABASE ERROR AND ANSWERED HTTP 200.** `handleLibraryBooks` sent
//     `200 { source: "error", error, categories: [], books: [] }`, and the Library's ONLY path to its
//     error state was `if (!response.ok) throw` — **which a 200 never is.** T8-9 shipped a
//     three-state load (`loading` / `ok` / `error`) with its own comment saying *"an empty shelf and
//     a shelf that FAILED TO LOAD are not the same sentence … the owner saw the video shelf
//     disappear and this line told her the videos had not been added yet — the app stating as fact
//     something it had no way of knowing."* The state, the sentence and both translations all
//     shipped. **The server's own 200 made the `error` arm unreachable on the exact failure it was
//     written for**, so on a database outage the video shelf still says
//
//         "The Video Library is ready. Class videos will appear here on their own shelves once
//          they're added."
//
//     word for word the sentence that fix exists to kill. **A payload naming its own failure
//     (`source: "error"`) does not make the answer honest if nothing reads the name.**
//
// (2) **`source` HAS THREE VALUES AND WAS READ AS A BOOLEAN.** The client tested `source === "db"`
//     and put everything else in one `else` — so `"fallback"` (**a deliberate answer**: the database
//     is not configured, use your in-code catalog) and `"error"` (**we could not ask**) became the
//     same thing. R18's distinction, on a discriminator the server was already sending. And the else
//     is the expensive one: it substitutes `src/catalogs/sacred-text-books.ts`, **47 titles whose
//     28.8M characters of page text live in the database that just refused**, with the `pageCount > 0`
//     filter skipped because `usingDbData` is false. So a database outage draws a **full shelf of
//     books that do not open** — R60's Card Library finding again, the failure state is not a missing
//     sentence but a wrong one, and here the wrong thing is a whole library.
//
// **THE FALLBACK IS KEPT AND IS NOT THE DEFECT.** A house that has never had a database SHOULD show
// its in-code catalogue; that is what `source: "fallback"` means and it is untouched. What may not
// happen is a FAILED read borrowing that answer.
//
// **THREE ANSWERS, ARGUED RATHER THAN COPIED (R58/R70's ruling).** This route is OPEN — the tier
// matrix has `featureForRoute("GET", "/api/library/books")` at `null` and it refuses nobody — so a
// `sign-in` branch would be a second answer key for a refusal it cannot make.
//
// **THE CLIENT FIX DOES NOT DEPEND ON THE SERVER FIX LANDING**, deliberately: a 200 still carrying
// `source: "error"` is read as an error here, so the two halves are independently correct rather than
// jointly necessary.
//
// `doFetch` is a PARAMETER (R46), so every answer this route can give is reachable with no browser,
// no session and no database.

export type LibraryMedia = "book" | "video";

export type LibraryShelfAnswer<T> = {
  // "db"       — the catalogue arrived. `books` is it.
  // "fallback" — the server has no database for this and says so. The in-code catalogue IS the answer.
  // "error"    — we could not ask. **NOT a shelf, and never the in-code catalogue** (see above).
  kind: "db" | "fallback" | "error";
  books: T[];
};

export const libraryShelfPath = "/api/library/books";

export const libraryShelfUrl = (media: LibraryMedia) =>
  `${libraryShelfPath}?media=${encodeURIComponent(media)}`;

export async function readLibraryShelf<T>(
  media: LibraryMedia,
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<LibraryShelfAnswer<T>> {
  let response: Response;
  try {
    response = await doFetch(libraryShelfUrl(media));
  } catch {
    // R18/R63: a request that never landed is TRANSPORT NOISE, not the server's word about the shelf.
    return { kind: "error", books: [] };
  }
  if (!response.ok) { await response.body?.cancel?.().catch(() => {}); return { kind: "error", books: [] }; }
  const data = await response.json().catch(() => null) as
    | { source?: unknown; books?: unknown }
    | null;
  if (!data) return { kind: "error", books: [] };
  // The server's own name for what it did. Anything it does not name is an error, never a shelf.
  if (data.source === "fallback") return { kind: "fallback", books: [] };
  // PRESENCE, not truthiness (R53): a 200 that carries no `books` ARRAY is not a catalogue, whatever
  // it calls itself.
  if (data.source !== "db" || !Array.isArray(data.books)) return { kind: "error", books: [] };
  return { kind: "db", books: data.books as T[] };
}

// WHAT THE SHELF IS BUILT FROM, AND IT IS THE HALF THE ROUND IS ABOUT.
//
// `null` is R39's third state and means "still loading" — the in-code catalogue is drawn while the
// read is in flight, which is why the Library has always felt instant, and that is kept.
// **AN ERROR IS NOT A CATALOGUE.** Held here rather than at the call site so no later spelling can
// put the substitution back in front of the guard (R52: by POSITION, not by wording).
export function shelfSource<T>(kind: "loading" | "db" | "fallback" | "error", fromDb: T[] | null, inCode: T[]): T[] {
  if (kind === "error") return [];
  return fromDb ?? inCode;
}
